import { Component, OnInit } from '@angular/core';
import { InventoryTypeProxyService } from '../../proxies/inventory/inventory-type.proxy.service';
import { InventoryType } from '../../models/inventory/inventory-type';
import { ModalService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { InventoryTypeModalComponent } from '../common/modals/inventory-type-modal/inventory-type-modal.component';
import { ConfirmationService, MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Translation } from '../../models/translation';
import { LocalHelperService } from '../../services/local-helper.service';
import { UserGroup } from '../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-inventory-type',
  templateUrl: './inventory-type.component.html',
  styleUrl: './inventory-type.component.scss',
})
export class InventoryTypeComponent implements OnInit {
  isLoading = false;
  allInventoryTypes: InventoryType[] = [];
  translations: Record<string, Translation> = {
    Danger_Zone_Title: {
      en: 'Danger Zone',
      es: 'Cuidado',
    },
    Danger_Zone_Message: {
      en: 'Do you want to delete this Inventory Type?',
      es: '¿Desea eliminar este tipo de inventario?',
    },
    Cancel: {
      en: 'Cancel',
      es: 'Cancelar',
    },
    Delete: {
      en: 'Delete',
      es: 'Eliminar',
    },
    Success_Delete_Message: {
      en: 'Inventory Type {{name}} Deleted',
      es: 'Tipo de inventario {{name}} eliminado',
    },
  };

  constructor(
    private inventoryTypeProxyService: InventoryTypeProxyService,
    private modalService: ModalService,
    private confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private translateService: TranslateService,
    public localHelperTranslate: LocalHelperService,
  ) {}

  canEdit() {
    // if Admin or Editor
    if (AuthService.hasRole(UserGroup.InventoryTypesAdmin)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(UserGroup.InventoryTypesViewer)) {
      return false;
    }
    // Default
    return false;
  }

  ngOnInit(): void {
    this.getAllInventoryTypes();
  }

  getAllInventoryTypes() {
    this.isLoading = true;
    this.inventoryTypeProxyService.getAll().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success && response.data) {
          this.allInventoryTypes = response.data;
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.log('🚀 ~ InventoryTypeComponent ~ this.inventoryTypeProxyService.getAll ~ error:', error);
      },
    });
  }

  openInventoryTypeModal(inventoryType?: InventoryType) {
    this.modalService
      .open(
        InventoryTypeModalComponent,
        { modalClass: 'modal_small' },
        {
          inventoryType: structuredClone(inventoryType),
        },
      )
      .subscribe((result) => {
        if (result && result.action === 'save' && result.data) {
          const index = this.allInventoryTypes.findIndex((it) => it.id === result.data?.id);
          if (index !== -1) {
            this.allInventoryTypes.splice(index, 1, result.data); // Replace the existing item
          } else {
            this.allInventoryTypes.push(result.data); // Add the item if it doesn't exist
          }
          this.allInventoryTypes = [...this.allInventoryTypes]; // 🔁 Force PrimeNG to recalculate paginator
        }
      });
  }

  deletePopup(event: Event, inventoryType: InventoryType) {
    event.stopPropagation();
    this.confirmationService.confirm({
      key: 'deleteConfirm',
      target: event.target as EventTarget,
      message: this.translations['Danger_Zone_Message'][this.translateService.currentLang as keyof Translation],
      header: this.translations['Danger_Zone_Title'][this.translateService.currentLang as keyof Translation],
      icon: 'pi pi-info-circle',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: this.translations['Cancel'][this.translateService.currentLang as keyof Translation],
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translations['Delete'][this.translateService.currentLang as keyof Translation],
        severity: 'danger',
      },

      accept: () => {
        this.deleteInventoryType(inventoryType);
      },
    });
  }

  deleteInventoryType(inventoryType: InventoryType) {
    this.inventoryTypeProxyService.deleteInventoryTypeById(inventoryType.id).subscribe({
      next: (response) => {
        if (response.success) {
          const index = this.allInventoryTypes.findIndex((c) => c.id === inventoryType.id);
          if (index !== -1) {
            this.allInventoryTypes.splice(index, 1);
          }

          this.messageService.add({
            severity: 'success',
            summary: this.translations['Success_Delete_Message'][this.translateService.currentLang as keyof Translation].replace(
              '{{name}}',
              this.localHelperTranslate.getLocalizedValue(inventoryType.name),
            ),
            key: 'br',
            life: 2500,
          });
        }
      },
      error: (error) => {
        console.error('❌ Error deleting Inventory Type:', error);
      },
    });
  }

  localizedSort(event: SortEvent) {
    if (event.data) {
      event.data.sort((a, b) => {
        if (event.order) {
          const aName = this.localHelperTranslate.getLocalizedValue(a.name) || '';
          const bName = this.localHelperTranslate.getLocalizedValue(b.name) || '';
          return event.order * aName.localeCompare(bName);
        } else {
          return 0;
        }
      });
    }
  }
}
